import { Brain, Award, Users, FileText } from 'lucide-react';
import { Hero } from '../../components/Hero';

export function Overview() {
  return (
    <div>
      <Hero
        title="About Juncos Neuro Consulting"
        description="A distinguished neurological center committed to clinical excellence, pioneering research, and personalized patient care. Our evidence-based approach combines decades of expertise with state-of-the-art diagnostics to deliver optimal outcomes."
        image="https://images.unsplash.com/photo-1504813184591-01572f98c85f?auto=format&fit=crop&q=80"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid md:grid-cols-2 gap-12 mb-16">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Excellence in Neurological Care</h2>
            <p className="text-gray-600 mb-6">
              Under the distinguished leadership of Dr. Jorge L. Juncos, MD, our practice has 
              established itself as a premier destination for neurological care. Dr. Juncos brings 
              over four decades of unparalleled expertise in neurology, with particular emphasis on 
              movement disorders, neurodegenerative diseases, and complex neurological conditions.
            </p>
            <p className="text-gray-600 mb-6">
              As a former Professor of Neurology at Emory University School of Medicine and Director 
              of their Movement Disorders Program, Dr. Juncos has contributed significantly to the 
              field through groundbreaking research, particularly in Parkinson's disease and related 
              disorders. His research has been funded by the National Institutes of Health and 
              numerous private foundations, leading to over 112 peer-reviewed publications that have 
              advanced our understanding of neurological disorders.
            </p>
            <p className="text-gray-600">
              Dr. Juncos' commitment to patient care is reflected in his innovative treatment 
              approaches, combining traditional neurological expertise with cutting-edge therapeutic 
              modalities. His comprehensive understanding of complex neurological conditions and 
              dedication to personalized patient care has earned him recognition as one of Atlanta's 
              Top Doctors for multiple consecutive years.
            </p>
          </div>
          <div className="bg-blue-50 rounded-lg p-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">By the Numbers</h3>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="text-3xl font-bold text-blue-600">40+</p>
                <p className="text-gray-600">Years of Experience</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-blue-600">25k+</p>
                <p className="text-gray-600">Patients Treated</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-blue-600">112</p>
                <p className="text-gray-600">Research Publications</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-blue-600">99%</p>
                <p className="text-gray-600">Patient Satisfaction</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-8 mb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Professional Background & Achievements</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Academic Excellence</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• MD from Columbia University College of Physicians and Surgeons</li>
                <li>• Neurology Residency at Columbia Presbyterian Medical Center</li>
                <li>• Movement Disorders Fellowship at Columbia Presbyterian Medical Center</li>
                <li>• Former Professor of Neurology at Emory University School of Medicine</li>
                <li>• Director of Movement Disorders Program at Emory University (1988-2022)</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Specialized Expertise</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• Movement Disorders and Parkinson's Disease</li>
                <li>• Deep Brain Stimulation Management</li>
                <li>• Neurodegenerative Disorders</li>
                <li>• Complex Neurological Conditions</li>
                <li>• Clinical Research and Drug Development</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          <div className="bg-white rounded-lg p-6 shadow-md">
            <Brain className="h-10 w-10 text-blue-600 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Expert Care</h3>
            <p className="text-gray-600">
              Specialized treatment protocols developed by leading neurological experts.
            </p>
          </div>
          <div className="bg-white rounded-lg p-6 shadow-md">
            <Award className="h-10 w-10 text-blue-600 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Advanced Technology</h3>
            <p className="text-gray-600">
              State-of-the-art diagnostic and treatment equipment for precise care.
            </p>
          </div>
          <div className="bg-white rounded-lg p-6 shadow-md">
            <Users className="h-10 w-10 text-blue-600 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Personalized Approach</h3>
            <p className="text-gray-600">
              Individualized treatment plans tailored to each patient's needs.
            </p>
          </div>
          <div className="bg-white rounded-lg p-6 shadow-md">
            <FileText className="h-10 w-10 text-blue-600 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Research Focus</h3>
            <p className="text-gray-600">
              Continuous advancement through clinical research and studies.
            </p>
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-8 mb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Our Commitment to Excellence</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Patient-Centered Care</h3>
              <p className="text-gray-600 mb-4">
                We believe in treating the whole person, not just the condition. Our approach combines medical 
                expertise with compassionate care, ensuring each patient receives the attention and support they need.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Continuous Innovation</h3>
              <p className="text-gray-600 mb-4">
                Through ongoing research and adoption of new technologies, we stay at the forefront of neurological 
                treatment advances, offering our patients the most effective care options available.
              </p>
            </div>
          </div>
        </div>

        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Start Your Journey to Better Health</h2>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            Whether you're seeking treatment for a neurological condition or looking for expert consultation, 
            our team is here to help you achieve the best possible outcomes for your health.
          </p>
          <button className="bg-blue-600 text-white px-8 py-3 rounded-md font-semibold hover:bg-blue-700 transition-colors">
            Schedule a Consultation
          </button>
        </div>
      </div>
    </div>
  );
}